'use client';

/**
 * ThemeSwitcher Component
 * 
 * This component provides a button to toggle between light, dark, and system themes.
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
// We'll use the type interface directly without the import to avoid build issues
// import type { ThemeSwitcherBaseProps } from '@repo/design-system/types';

// Define the props interface directly to avoid build issues
interface ThemeSwitcherProps {
  /**
   * Additional class name for styling
   */
  className?: string;
  
  /**
   * Size of the theme switcher
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ThemeSwitcher component
 * 
 * A button group that allows selecting light, dark, and system themes.
 * 
 * @example
 * ```tsx
 * <ThemeSwitcher />
 * ```
 */
export const ThemeSwitcher = ({
  className,
  size = 'md',
}: ThemeSwitcherProps) => {
  // Only access theme data after mounting
  const [mounted, setMounted] = useState(false);
  // Maintain local state for the active theme button
  const [localTheme, setLocalTheme] = useState<string>('system');
  // Access theme data from next-themes
  const { theme, setTheme, resolvedTheme } = useTheme();

  // Set up after mounting to prevent SSR issues
  useEffect(() => {
    setMounted(true);
    // Get theme from localStorage if it exists
    if (typeof window !== 'undefined') {
      try {
        const savedTheme = localStorage.getItem('app-theme') || theme || 'system';
        setLocalTheme(savedTheme);
      } catch (error) {
        // If localStorage fails, use system theme
        setLocalTheme('system');
      }
    }
  }, []);

  // Update local theme when external theme changes
  useEffect(() => {
    if (mounted && theme) {
      setLocalTheme(theme);
    }
  }, [theme, mounted]);

  // Map size to class names for the container and buttons
  const sizeClasses = {
    sm: {
      container: 'h-8',
      button: 'h-8 w-8',
      icon: 'h-4 w-4'
    },
    md: {
      container: 'h-10',
      button: 'h-10 w-10',
      icon: 'h-5 w-5'
    },
    lg: {
      container: 'h-12',
      button: 'h-12 w-12',
      icon: 'h-6 w-6'
    }
  };

  const buttonBaseClasses = cn(
    'inline-flex items-center justify-center transition-all duration-200',
    'hover:bg-gray-100 dark:hover:bg-gray-800',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    'touch-manipulation relative overflow-hidden' // Improve touch response
  );

  const activeClasses = cn(
    'bg-white dark:bg-gray-800',
    'text-primary-600 dark:text-primary-400',
    'shadow-lg',
    'scale-110',
    'z-10'
  );
  
  const inactiveClasses = cn(
    'text-gray-600 dark:text-gray-400',
    'hover:scale-105'
  );

  /**
   * Safely change the theme
   * This function:
   * 1. Updates local state immediately for instant UI feedback
   * 2. Safely accesses localStorage and handles errors
   * 3. Delays the actual theme change to avoid SSR conflicts
   * 4. Works safely even if called during SSR
   */
  const handleThemeChange = (newTheme: string) => {
    // Prevent rapid clicks that could cause animation flicker
    if (newTheme === localTheme) return;
    
    // Update local UI state immediately
    setLocalTheme(newTheme); 
    
    // Only proceed if we're in the browser and mounted
    if (typeof window !== 'undefined' && mounted) {
      try {
        // Save theme preference to localStorage
        localStorage.setItem('app-theme', newTheme);
        
        // Apply theme change without delay to prevent flickering
        setTheme(newTheme);
      } catch (error) {
        console.error('Error setting theme:', error);
      }
    }
  };

  // Show a placeholder during SSR to prevent hydration mismatch
  if (!mounted) {
    return (
      <div 
        className={cn(
          'flex items-center gap-0.5 p-1',
          'bg-gray-100/80 dark:bg-gray-800/80',
          'rounded-lg',
          sizeClasses[size].container, 
          className
        )} 
      />
    );
  }

  return (
    <div 
      className={cn(
        'flex items-center gap-0.5 p-1',
        'bg-gray-100/80 dark:bg-gray-800/80',
        'rounded-lg backdrop-blur-sm',
        'shadow-inner',
        'h-8 w-[106px]', // Fixed dimensions to prevent layout shifts
        className
      )}
      role="group"
      aria-label="Theme switcher"
    >
      {/* System theme */}
      <button
        type="button"
        onClick={() => handleThemeChange('system')}
        className={cn(
          buttonBaseClasses,
          'w-[32px] h-8', // Fixed width
          'rounded-l-md',
          localTheme === 'system' ? activeClasses : inactiveClasses
        )}
        aria-label="Use system theme"
        aria-pressed={localTheme === 'system'}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          className={cn('h-4 w-4', 'transition-transform duration-150')}
          strokeWidth={1.5}
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" 
          />
        </svg>
      </button>

      {/* Light theme */}
      <button
        type="button"
        onClick={() => handleThemeChange('light')}
        className={cn(
          buttonBaseClasses,
          'w-[32px] h-8', // Fixed width
          localTheme === 'light' ? activeClasses : inactiveClasses
        )}
        aria-label="Use light theme"
        aria-pressed={localTheme === 'light'}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          className={cn('h-4 w-4', 'transition-transform duration-150')}
          strokeWidth={1.5}
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" 
          />
        </svg>
      </button>

      {/* Dark theme */}
      <button
        type="button"
        onClick={() => handleThemeChange('dark')}
        className={cn(
          buttonBaseClasses,
          'w-[32px] h-8', // Fixed width
          'rounded-r-md',
          localTheme === 'dark' ? activeClasses : inactiveClasses
        )}
        aria-label="Use dark theme"
        aria-pressed={localTheme === 'dark'}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          className={cn('h-4 w-4', 'transition-transform duration-150')}
          strokeWidth={1.5}
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" 
          />
        </svg>
      </button>
    </div>
  );
};

// Export as default component as well
export default ThemeSwitcher;
